import { some } from 'lodash/fp';
import React from 'react';
import { Field, Form } from 'react-final-form';

import Button from '@skippay/core/components/Button';
import FormattedCurrency from '@skippay/core/components/FormattedCurrency';
import Link from '@skippay/core/components/Link';
import Loader from '@skippay/core/components/Loader';
import Step from '@skippay/core/components/Step';
import Text from '@skippay/core/components/Text';
import { TOO_MANY_REQUESTS } from '@skippay/core/constants/httpStatusCodes';
import AuthPhoneField from '@skippay/core/form-fields/AuthPhoneField';
import AuthTextField from '@skippay/core/form-fields/AuthTextField';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';
import { be } from '@skippay/core/utils/classUtils';
import { focus as autofocus, onBeforeClick } from '@skippay/core/utils/domUtils';
import { parseName, parsePhoneNumber } from '@skippay/core/utils/formUtils';
import {
	checked,
	email,
	notFormattedFont,
	pipeValidators,
	required,
	withErrorMessage,
} from '@skippay/core/utils/validators';

import AlreadyHasAccountBox from 'components/AlreadyHasAccountBox';
import MarketingExtendedConsentCheckboxField from 'components/form/fields/MarketingExtendedConsentCheckboxField';
import RegistrationConsentCheckboxField from 'components/form/fields/RegistrationConsentCheckboxField';
import { InfoBox } from 'components/InfoBox';
import LoggedOutOnly from 'components/LoggedOutOnly';
import SEO from 'components/SEO';
import useAuthLinksByCountry from 'hooks/useAuthLinksByCountry';
import useInAppBrowserClose from 'hooks/useInAppBrowserClose';
import AuthLayout from 'layout/Auth';
import { usePromoContent } from 'screens/RegistrationScreen/usePromoContent';
import AuthErrors from 'sections/auth/Errors';
import AuthLoginRedirectButton from 'sections/auth/LoginRedirectButton';
import { registrationErrorFormat } from 'utils/form';

import AuthFooterFinishRegistration from './components/AuthFooterFinishRegistration';
import './index.sass';
import useRegistrationScreen, {
	FIELD_EMAIL,
	FIELD_GENERAL_CONSENT,
	FIELD_MARKETING_CONSENT,
	FIELD_NAME,
	FIELD_PHONE,
	INITIAL_VALUE_EMAIL,
	INITIAL_VALUE_FROM,
	INITIAL_VALUE_NAME,
	INITIAL_VALUE_PHONE,
} from './useRegistrationScreen';

const MODULE_NAME = 'RegistrationScreen';

function RegistrationScreen() {
	const intl = useIntl();
	useInAppBrowserClose();
	const { loginLink } = useAuthLinksByCountry();

	const {
		accountReferrerQuery,
		initialValues,
		isInitialValueSet,
		firstNameOnBlurValidator,
		emailOnBlurValidator,
		phoneOnBlurValidator,
		recaptchaNode,
		registrationError,
		registrationFormApi,
	} = useRegistrationScreen();

	const { loading } = accountReferrerQuery;
	const { locale } = useInitialCountryLocale();

	const content = usePromoContent();

	if (typeof window === 'undefined') {
		return <Loader />;
	}

	return (
		<LoggedOutOnly>
			<div {...(!loading && { style: { display: 'none' } })}>
				<Loader fullSize message="" />
			</div>
			<div {...(loading && { style: { display: 'none' } })}>
				<AuthLayout
					contentOnLeft
					footer={<AuthFooterFinishRegistration />}
					testId="registration"
					title={
						accountReferrerQuery.data ? (
							<FormattedMessage
								defaultMessage="{name} má pro vás slevu {amount}"
								id="RegistrationScreen.web.headingReferral"
								values={{
									amount: (
										<FormattedCurrency
											{...accountReferrerQuery.data.accountReferrer.discountPrice}
										/>
									),
									name: accountReferrerQuery.data.accountReferrer.fullName,
								}}
							/>
						) : (
							content.title
						)
					}
					underTitle={
						accountReferrerQuery.data ? (
							<FormattedMessage
								defaultMessage="Zaregistrujte se nyní a získejte slevu na první nákup"
								id="RegistrationScreen.web.subheadingReferral"
							/>
						) : (
							content.subtitle
						)
					}
					promoTitle={content.promoTitle}
					widerContainer
					image={content.image}
				>
					<SEO
						noUrlMeta
						title={intl.formatMessage({
							id: 'screens.RegistrationScreen.title',
							defaultMessage: 'Registrace',
						})}
					/>
					{recaptchaNode}
					<Form form={registrationFormApi}>
						{({
							form: {
								mutators: { triggerValidation },
							},
							hasValidationErrors,
							handleSubmit,
							submitting,
							submitError,
							validating,
						}) => {
							return (
								<form data-test-id="formRegistration" onSubmit={handleSubmit}>
									{submitting ? (
										<Loader message="" />
									) : registrationError?.status === TOO_MANY_REQUESTS ? (
										<>
											<Step v lg />
											<AuthErrors errors={registrationError.data.errors} />
											<Step v md />
											<AuthLoginRedirectButton />
										</>
									) : (
										<div className={be(MODULE_NAME, 'form')}>
											{some({ code: 'ALREADY_EXISTS' }, registrationError?.data.errors) ? (
												<AlreadyHasAccountBox
													link={
														<Link to={loginLink} testId="login">
															<FormattedMessage
																id="RegistrationScreen.web.alreadyExist.linkLogin"
																defaultMessage="Přihlaste se kliknutím zde"
															/>
														</Link>
													}
												/>
											) : some({ code: 'CZ_USER_EXISTS' }, registrationError?.data.errors) ? (
												<AlreadyHasAccountBox domain="CZ" />
											) : some({ code: 'SK_USER_EXISTS' }, registrationError?.data.errors) ? (
												<AlreadyHasAccountBox domain="SK" />
											) : (
												<>
													{isInitialValueSet && (
														<>
															<InfoBox>
																<Text>
																	{initialValues[INITIAL_VALUE_FROM] ? (
																		<FormattedMessage
																			id="RegistrationScreen.web.dataHaveBeenAdded"
																			defaultMessage="Údaje jsme doplnili za vás podle e-shopu {eshopName}, abyste&nbsp;to&nbsp;měli bez práce."
																			values={{
																				eshopName: initialValues[INITIAL_VALUE_FROM],
																			}}
																		/>
																	) : (
																		<FormattedMessage
																			id="RegistrationScreen.web.dataHaveBeenAdded.unknownEshop"
																			defaultMessage="Údaje jsme doplnili za vás podle e-shopu abyste&nbsp;to&nbsp;měli bez práce."
																		/>
																	)}
																</Text>
															</InfoBox>
															{submitError && <Step v sm />}
														</>
													)}

													{submitError && <AuthErrors errors={submitError} />}
												</>
											)}

											{(isInitialValueSet || submitError) && <Step v sm />}

											<Field
												animatedInput
												autoFocus
												component={AuthTextField}
												initialValue={initialValues[INITIAL_VALUE_NAME]}
												larger
												name={FIELD_NAME}
												onBlur={() => triggerValidation(FIELD_NAME)}
												onRef={autofocus}
												parse={parseName}
												placeholder={intl.formatMessage({
													id: 'RegistrationScreen.field.name.placeholder',
													defaultMessage: 'Jméno a příjmení',
												})}
												showExclamationWhenInvalid
												required
												validate={(value, values, meta) => {
													return pipeValidators(
														required,
														notFormattedFont,
														firstNameOnBlurValidator
													)(value, values, meta);
												}}
												wide
											/>
											<Field
												animatedInput
												component={AuthTextField}
												initialValue={initialValues[INITIAL_VALUE_EMAIL]}
												larger
												name={FIELD_EMAIL}
												onBlur={() => triggerValidation(FIELD_EMAIL)}
												placeholder={intl.formatMessage({
													id: 'RegistrationScreen.field.email.placeholder',
													defaultMessage: 'E-mailová adresa',
												})}
												showExclamationWhenInvalid
												type="email"
												required
												validate={(value, values, meta) => {
													// need the extra wrap to function, otherwise final-form does not pass `meta` parameter
													return pipeValidators(required, email, emailOnBlurValidator)(
														value,
														values,
														meta
													);
												}}
												wide
											/>
											<Field
												animatedLabel={intl.formatMessage({
													id: 'RegistrationScreen.field.phone.placeholder',
													defaultMessage: 'Vaše telefonní číslo',
												})}
												aria-label={intl.formatMessage({
													id: 'RegistrationScreen.field.phone.ariaLabel',
													defaultMessage: 'Vaše telefonní číslo',
												})}
												component={AuthPhoneField}
												initialValue={initialValues[INITIAL_VALUE_PHONE]}
												name={FIELD_PHONE}
												onBlur={() => triggerValidation(FIELD_PHONE)}
												parse={parsePhoneNumber}
												showExclamationWhenInvalid
												type="tel"
												required
												validate={(value, values, meta) => {
													// need the extra wrap to function, otherwise final-form does not pass `meta` parameter
													return pipeValidators(required, phoneOnBlurValidator)(
														value,
														values,
														meta
													);
												}}
												wide
												bold
											/>
											<Text color="light-gray-500">
												<FormattedMessage
													id="RegistrationScreen.web.infoAboutPhoneNumber"
													defaultMessage="Na toto číslo budeme posílat SMS s kódem pro schválení nákupů, přihlašování se do Skip Pay apod."
												/>
											</Text>
											<Step v md className="d-none d-sm-block" />
											<Step v md smaller className="d-sm-none" />
											<Field
												component={MarketingExtendedConsentCheckboxField}
												name={FIELD_MARKETING_CONSENT}
												isCheckbox
											/>

											<Field
												locale={locale}
												component={RegistrationConsentCheckboxField}
												isCheckbox
												name={FIELD_GENERAL_CONSENT}
												validate={withErrorMessage(registrationErrorFormat, checked)}
											/>

											<Step v md larger />
											<Button
												disabled={hasValidationErrors}
												isBusy={validating}
												name="submit"
												testId="submit"
												type="button"
												width="full"
												// There is a need to postpone form submission to prevent immediate submit in case there is a
												// onBlur validation in process. It will allow onBlur validation to start and set form state as
												// validating. Once blur validation is finished, form will be submitted as usual.
												{...onBeforeClick(() => {
													setTimeout(handleSubmit, 0);
												})}
											>
												<FormattedMessage
													id="RegistrationScreen.web.createAccount"
													defaultMessage="Vytvořit registraci"
												/>
											</Button>
											<Step v sm larger />
											<Text align="center">
												<FormattedMessage
													id="RegistrationScreen.web.alreadyHaveAccount"
													defaultMessage="Už máte registraci? {link}."
													values={{
														link: (
															<Link variant="inherit" testId="login" to={loginLink}>
																<FormattedMessage
																	id="RegistrationScreen.web.login"
																	defaultMessage="Přihlaste se"
																/>
															</Link>
														),
													}}
												/>
											</Text>
										</div>
									)}
								</form>
							);
						}}
					</Form>
				</AuthLayout>
			</div>
		</LoggedOutOnly>
	);
}

export default RegistrationScreen;
